<template>
  <v-dialog v-model="dialog" max-width="720px" persistent scrollable :content-class="dialogContentClass">
    <template v-slot:activator="{ on }">
      <v-btn color="primary" depressed v-on="on"> <v-icon left>add</v-icon>New Asset </v-btn>
    </template>
    <v-card class="asset-dialog-card">
      <v-form ref="form" class="asset-dialog-form">
        <v-card-title class="asset-dialog__title">
          <span class="asset-dialog__title-icon">
            <v-icon color="white">mdi-database-plus-outline</v-icon>
          </span>
          <span>
            <span class="asset-dialog__title-text d-block">Create Asset</span>
            <span class="asset-dialog__subtitle d-block">Create reusable data for your automations</span>
          </span>
        </v-card-title>
        <v-card-text class="asset-dialog__body">
          <v-alert v-show="!!error" text prominent type="error" icon="mdi-alert-circle">{{ error }}</v-alert>
          <v-alert v-show="!!success" text prominent type="success" icon="mdi-cloud-check">Succesfully Created</v-alert>
          <v-container class="text-center" v-if="loading">
            <v-progress-circular :size="70" :width="7" color="purple" indeterminate></v-progress-circular>
          </v-container>
          <v-container v-else fluid>
            <section class="asset-section">
              <div class="asset-section__head">
                <span class="asset-section__icon"><v-icon small color="white">mdi-information-outline</v-icon></span>
                <span>
                  <span class="asset-section__title d-block">Asset details</span>
                  <span class="asset-section__hint d-block">Give the asset a clear and reusable identity</span>
                </span>
              </div>
              <v-row dense>
                <v-col cols="12" md="6">
                <v-text-field
                  v-model="item.Name"
                  label="Name"
                  placeholder="e.g. Muavin_1001"
                  outlined
                  dense
                  required
                  :rules="nameRules"
                  maxlength="50"
                  counter
                  :disabled="success"
                  @keypress="nameControl($event)"
                ></v-text-field>
              </v-col>
                <v-col cols="12" md="6">
                <v-text-field
                  v-model="item.Description"
                  label="Description"
                  outlined
                  dense
                  maxlength="200"
                  counter
                  :disabled="success"
                ></v-text-field>
              </v-col>
              </v-row>
            </section>

            <section class="asset-section">
              <div class="asset-section__head">
                <span class="asset-section__icon"><v-icon small color="white">mdi-tag-multiple-outline</v-icon></span>
                <span>
                  <span class="asset-section__title d-block">Labels</span>
                  <span class="asset-section__hint d-block">Organize and find this asset more easily</span>
                </span>
              </div>
              <div class="asset-label-row">
                <v-autocomplete
                  v-model="item.Labels"
                  :items="labels"
                  item-text="Name"
                  item-value="Id"
                  outlined
                  dense
                  multiple
                  label="Select Labels"
                  hide-details
                  :disabled="success"
                ></v-autocomplete>
                <v-btn
                  v-if="hasAuth('Label Action')"
                  class="asset-label-button"
                  color="primary"
                  outlined
                  v-on:click="openLabelDialog"
                  :disabled="success"
                >
                  <v-icon small left>mdi-tag-plus-outline</v-icon>Manage
                </v-btn>
              </div>
            </section>

            <section class="asset-section">
              <div class="asset-section__head">
                <span class="asset-section__icon"><v-icon small color="white">mdi-shape-outline</v-icon></span>
                <span>
                  <span class="asset-section__title d-block">Type and value</span>
                  <span class="asset-section__hint d-block">Choose how this asset stores its data</span>
                </span>
              </div>
              <v-row dense>
                <v-col cols="12">
                <v-autocomplete
                  v-model="item.AssetType"
                  :items="types"
                  item-text="Name"
                  item-value="Id"
                  outlined
                  dense
                  label="Select Type"
                  :rules="typeRules"
                  :disabled="success"
                  v-on:change="changeType"
                  return-object
                ></v-autocomplete>
              </v-col>
                <v-col v-if="textType" cols="12">
                <v-text-field
                  v-model="item.Value"
                  label="Value"
                  outlined
                  dense
                  maxlength="200"
                  counter
                  :disabled="success"
                ></v-text-field>
              </v-col>
                <v-col v-if="credentialType" cols="12" sm="6">
                <v-text-field
                  v-model="item.Username"
                  label="Username"
                  outlined
                  dense
                  maxlength="100"
                  counter
                  :disabled="success"
                ></v-text-field>
              </v-col>
                <v-col v-if="credentialType" cols="12" sm="6">
                <v-text-field
                  v-model="item.Password"
                  label="Password"
                  outlined
                  dense
                  maxlength="500"
                  counter
                  type="password"
                  :disabled="success"
                ></v-text-field>
              </v-col>
            </v-row>
            </section>
          </v-container>
        </v-card-text>
        <v-card-actions class="asset-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="toggleDialog">Cancel</v-btn>
          <v-btn depressed color="primary" v-show="!success && !loading" @click="save">
            <v-icon small left>mdi-content-save-outline</v-icon>Save
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { FETCH_LABELS, FETCH_TYPES, CREATE_ASSET, TOGGLE_CREATE_ASSET_DIALOG, TOGGLE_LABEL_DIALOG, CHANGE_DIALOG } from "../../store/modules/asset";
import { hasActionAuthorization } from "../../authorization";

export default {
  data: () => ({
    item: {
      Name: "",
      Description: "",
      AssetType: {},
      AssetTypeId: "",
      Labels: [],
      Value: "",
      Username: "",
      Password: "",
    },
    textType: false,
    credentialType: false,
    nameRules: [
      (v) => !!v || "Asset name is required",
      (v) =>
        /^[A-Za-z_][A-Za-z0-9_]*$/.test(v) ||
        "Name must start with a letter or underscore; only letters, numbers and underscore allowed (e.g. Muavin_1001)",
    ],
    typeRules: [(v) => !!v || "Type is required"],
  }),
  computed: {
    dialog: {
      get() {
        return this.$store.state.asset.createAsset.dialog;
      },
      set() {
        this.toggleDialog();
      },
    },
    ...mapState("asset", {
      labels: (state) => state.labels,
      types: (state) => state.types,
      loading: (state) => state.createAsset.isLoading,
      success: (state) => state.createAsset.success,
      error: (state) => state.createAsset.error,
      labelSheetOpen: (state) => state.labelDialog,
    }),
    dialogContentClass() {
      return this.labelSheetOpen ? "asset-dialog asset-dialog--shifted" : "asset-dialog";
    },
  },
  created() {
    this.fetchTypes();
    this.fetchLabels();
  },
  methods: {
    ...mapActions("asset", {
      fetchLabels: FETCH_LABELS,
      fetchTypes: FETCH_TYPES,
      createAsset: CREATE_ASSET,
      toggleDialog: TOGGLE_CREATE_ASSET_DIALOG,
      toggleLabelDialog: TOGGLE_LABEL_DIALOG,
      changeDialog: CHANGE_DIALOG,
    }),
    save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      let data = {
        ...this.item,
        AssetTypeId: this.item.AssetType.Id,
      };
      delete data.AssetType;

      this.createAsset(data);
    },
    changeType() {
      if (!this.item.AssetType) {
        this.clearTextType();
        this.clearCredentialType();
        return;
      }

      if (this.item.AssetType.Name === "Text") {
        this.clearCredentialType();
        this.textType = true;
      } else if (this.item.AssetType.Name === "Credential") {
        this.clearTextType();
        this.credentialType = true;
      } else {
        this.clearTextType();
        this.clearCredentialType();
      }
    },
    clearTextType() {
      this.textType = false;
      this.item.Value = "";
    },
    clearCredentialType() {
      this.credentialType = false;
      this.item.Username = "";
      this.item.Password = "";
    },
    hasAuth(action) {
      return hasActionAuthorization(this.$store, "Assets", action);
    },
    openLabelDialog() {
      this.toggleLabelDialog();
    },
    nameControl(event) {
      if (!event.key || event.key.length !== 1) return;
      const current = this.item.Name || "";
      const allowed = current.length === 0 ? /[A-Za-z_]/ : /[A-Za-z0-9_]/;
      if (!allowed.test(event.key)) {
        event.preventDefault();
      }
    },
  },
};
</script>
